import { useState } from 'react';
import type { RescueDocumentType, RescueRequiredDocument } from '../../lib/documentQueries';
import { stateDocumentHint, stateDocumentKind, stateName, withFileLimit } from '../../lib/documentLabels';
import DocumentKindPicker from '../../components/Documents/DocumentKindPicker';
import { FileButton, FileCard, FileLink } from '../../components/Documents/FileCard';
import StatusPill from '../../components/Documents/StatusPill';
import UploadBox from '../../components/Documents/UploadBox';
import { certificatePill, certificateSubtitle, tone } from './DocumentRow';
import styles from './Account.module.scss';

const UPDATE_TYPES: RescueDocumentType[] = ['STATE_EXEMPTION', 'STATE_EXEMPTION_INELIGIBLE'];

/**
 * A conditional state where the rescue has filed proof of application.
 *
 * The application stays until the Fi team approves what replaces it, so an update sits beside it in
 * review rather than taking its place, and orders keep going in the meantime.
 */
export default function ApplicationRow({
  application,
  update,
  covered,
  uploading,
  onPickFile,
}: {
  application: RescueRequiredDocument;
  /** The certificate or proof of ineligibility filed to replace the application, if any. */
  update: RescueRequiredDocument | null;
  /** The application is standing in for a certificate the rescue still owes. */
  covered: boolean;
  uploading: boolean;
  onPickFile: (type: RescueDocumentType) => void;
}) {
  const [updating, setUpdating] = useState(false);
  const [chosen, setChosen] = useState<RescueDocumentType>('STATE_EXEMPTION');
  const doc = application.document;
  if (!doc) {
    return null;
  }

  const where = application.state ? stateName(application.state) : 'the state';
  const pending = update?.document?.status === 'PENDING' ? update.document : null;
  const rejected = update?.document?.status === 'REJECTED' ? update.document : null;
  // A rejected application is replaced with another application; anything else is an update.
  const replaceApplication = doc.status === 'REJECTED' || doc.expired;

  return (
    <div className={styles.stateRow}>
      <div className={styles.stateRowMain}>
        <div className={styles.stateName}>
          <b>{where}</b>
        </div>
        <FileCard
          name={doc.originalFileName}
          sub={`${stateDocumentKind('STATE_EXEMPTION_APPLICATION')}\n${certificateSubtitle(doc)}`}
        >
          <StatusPill tone={tone(doc)}>{certificatePill(doc)}</StatusPill>
          <FileLink href={doc.downloadUrl}>View</FileLink>
          {replaceApplication && (
            <FileButton onClick={() => onPickFile('STATE_EXEMPTION_APPLICATION')} disabled={uploading}>
              {uploading ? 'Uploading…' : 'Replace'}
            </FileButton>
          )}
          {!replaceApplication && !pending && !updating && (
            <FileButton onClick={() => setUpdating(true)} disabled={uploading}>
              Update
            </FileButton>
          )}
        </FileCard>
      </div>

      {doc.status === 'REJECTED' && doc.rejectionReason && (
        <div className={styles.rejectionNote}>
          <b>Reason:</b> {doc.rejectionReason}
        </div>
      )}

      {covered && !pending && (
        <div className={styles.reminderNote}>
          Your application covers Fi Nano orders for now. When {where} decides, upload your exemption certificate, or
          proof of ineligibility if it turned you down.
        </div>
      )}

      {pending && update && (
        <div className={styles.updateBlock}>
          <div className={styles.stateRowMain}>
            <div className={styles.stateName} />
            <FileCard
              name={pending.originalFileName}
              sub={`${stateDocumentKind(update.documentType)}\n${certificateSubtitle(pending)}`}
            >
              <StatusPill tone="pending">In review</StatusPill>
              <FileLink href={pending.downloadUrl}>View</FileLink>
            </FileCard>
          </div>
          <div className={styles.reminderNote}>
            Your proof of application stays on file until the Fi team approves your{' '}
            {stateDocumentKind(update.documentType).toLowerCase()}.
          </div>
        </div>
      )}

      {rejected && update && !pending && (
        <div className={styles.rejectionNote}>
          Your {stateDocumentKind(update.documentType).toLowerCase()} wasn't accepted
          {rejected.rejectionReason ? `: ${rejected.rejectionReason}` : '.'} Upload another with Update.
        </div>
      )}

      {updating && !pending && (
        <div className={`${styles.updateBlock} ${styles.uploadChoice}`}>
          <UploadBox
            compact
            title={`Upload your ${stateDocumentKind(chosen).toLowerCase()}`}
            hint={withFileLimit(
              stateDocumentHint(chosen, application.state) ??
                `The exemption certificate ${where} issued to your organization.`,
              chosen,
            )}
            onUpload={() => onPickFile(chosen)}
            uploading={uploading}
            footer={<DocumentKindPicker accepted={UPDATE_TYPES} value={chosen} onChange={setChosen} />}
          />
        </div>
      )}
    </div>
  );
}
